import React, { FC, useMemo, useRef } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import translate from '@translate';
import { ReturnProductType } from 'src/types/return';
import { colors, sizes } from 'src/themes/default';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import Select from 'src/components/Select';
import { FormErrorsType } from '../../types/global';
import { SxProps, Theme } from '@mui/material/styles';
import ProductNameLink from 'src/components/ProductNameLink';
import {
    isEmptyProductQuantityInput,
    ProductQuantityInputValue,
    shouldApplyNonNegativeProductQuantityInput,
    shouldApplyProductQuantityInput,
    shouldRestoreNonNegativeProductQuantityOnBlur,
    shouldRestoreProductQuantityOnBlur,
    toProductQuantityInputValue,
} from 'src/utils/productQuantityInput';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import colorOptionFce from 'src/utils/colorOptionFce';

export type EditKeysType = 'QUANTITY' | 'RECEIVED' | 'RETURN_REASON' | 'RESOLUTION' | 'SERIAL_NUMBER';

const quantityColumnStyle = { width: '1%', minWidth: '130px' };
const reasonColumnStyle = { width: '1%', minWidth: '280px' };
const resolutionColumnStyle = { width: '1%', minWidth: '400px' };

const resolutionSelectSx: SxProps<Theme> = {
    '& .MuiInputBase-root': {
        overflow: 'hidden',
    },
    '& .MuiSelect-select': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
};

const buildReturnProductColumns = (): TableColumnType[] => [
    { key: 'PRODUCT_ID', label: 'id', type: 'id', defaultShow: true },
    { key: 'SKU', label: 'sku', defaultShow: true },
    { key: 'NAME', label: 'name', customRender: true, defaultShow: true },
    { key: 'EAN', label: 'EAN', defaultShow: true },
    {
        key: 'PRODUCT_TYPE',
        label: 'productType',
        type: 'select',
        selectOption: 'productTypeCodes',
        defaultShow: true,
    },
    {
        key: 'COUNT',
        label: 'count',
        type: 'number-filter',
        customStyle: quantityColumnStyle,
        customStyleHeader: quantityColumnStyle,
        defaultShow: true,
    },
    {
        key: 'QUANTITY',
        label: 'quantity',
        type: 'number-filter',
        customRender: true,
        customStyle: quantityColumnStyle,
        customStyleHeader: quantityColumnStyle,
        defaultShow: true,
    },
    {
        key: 'RECEIVED',
        label: 'received',
        type: 'number-filter',
        customRender: true,
        customStyle: quantityColumnStyle,
        customStyleHeader: quantityColumnStyle,
        defaultShow: true,
    },
    {
        key: 'RETURN_REASON',
        label: 'returnReason',
        customRender: true,
        type: 'select',
        selectOption: 'retunReasonCodes',
        customStyle: reasonColumnStyle,
        customStyleHeader: reasonColumnStyle,
        defaultShow: true,
    },
    {
        key: 'RESOLUTION',
        label: 'resolution',
        customRender: true,
        type: 'select',
        selectOption: 'returnResolutionCodes',
        customStyle: resolutionColumnStyle,
        customStyleHeader: resolutionColumnStyle,
        defaultShow: true,
    },
    {
        key: 'SERIAL_NUMBER',
        label: 'serialNumber',
        type: 'string',
        customRender: true,
        defaultShow: true,
    },
    { key: 'WEIGHT', label: 'weight', type: 'number-filter', defaultShow: false },
    { key: 'HS_CODE', label: 'hsCode', defaultShow: false },
    {
        key: 'AGECHECK',
        label: 'agecheck',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    { key: 'DESCRIPTION', label: 'description', defaultShow: false },
    {
        key: 'EXP_METHOD',
        label: 'expeditionMethod',
        type: 'select',
        selectOption: 'expeditionMethodCodes',
        defaultShow: false,
    },
    {
        key: 'INSERT_TYPE',
        label: 'insertType',
        type: 'select',
        selectOption: 'productInsetTypeCodes',
        defaultShow: false,
    },
    { key: 'ROP', label: 'ROP', type: 'number-filter', defaultShow: false },
    { key: 'EXPIRATION_DAYS', label: 'EXPIRATION_DAYS', type: 'number-filter', defaultShow: false },
    {
        key: 'SCAN_IMEI_SN_TYPE',
        label: 'scanSerialNumber',
        type: 'select',
        selectOption: 'scanImeiSnCodes',
        defaultShow: false,
    },
    { key: 'SN_REGEX', label: 'snRegex', defaultShow: false },
    { key: 'WIDTH', label: 'width', type: 'number-filter', defaultShow: false },
    { key: 'HEIGHT', label: 'height', type: 'number-filter', defaultShow: false },
    { key: 'LENGTH', label: 'length', type: 'number-filter', defaultShow: false },
    { key: 'PRICE', label: 'catalogPrice', type: 'number-filter', defaultShow: false },
    {
        key: 'UNIT_PRICE',
        label: 'unitPrice',
        type: 'select',
        selectOption: 'currencyCodes',
        defaultShow: false,
    },
    {
        key: 'BATTERY_TYPE',
        label: 'batteryType',
        type: 'select',
        selectOption: 'batteryTypeCodes',
        defaultShow: false,
    },
    { key: 'CUSTOM_FIELD_1', label: 'customField1', defaultShow: false },
    { key: 'CUSTOM_FIELD_2', label: 'customField2', defaultShow: false },
    { key: 'CUSTOM_FIELD_3', label: 'customField3', defaultShow: false },
    {
        key: 'ACTIVE',
        label: 'active',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    { key: 'CREATED_AT', label: 'createdAt', type: 'dateTime', defaultShow: false },
    { key: 'UPDATED_AT', label: 'updatedAt', type: 'dateTime', defaultShow: false },
    {
        key: 'IS_DELETED',
        label: 'isDeleted',
        type: 'select',
        selectOption: 'boolNumberCodes',
        defaultShow: false,
    },
    { key: 'DELETED_AT', label: 'deletedAt', type: 'dateTime', defaultShow: false },
];

export interface ProductsTablePropsType {
    data?: ReturnProductType[];
    newReturn: boolean;
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    returnResolutionCodes: ConstantsCodeType[];
    retunReasonCodes: ConstantsCodeType[];
    editErrors: FormErrorsType;
    permissionEditAll: boolean;
    skladonUser?: number;
}

const ProductsTable: FC<ProductsTablePropsType> = (props) => {
    const dispatch = useDispatch();
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const productTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.productTypeCodes);
    const expeditionMethodCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.expeditionMethodCodes
    );
    const productInsetTypeCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productInsetTypeCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const scanImeiSnCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.scanImeiSnCodes);
    const batteryTypeCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.batteryTypeCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);

    const allColumns = useMemo(() => buildReturnProductColumns(), []);

    const getInputName = (id: number, key: string) => `product-${id}-${key}`;
    const quantityOnFocusRef = useRef<Record<string, number>>({});
    const lastQuantityInputRef = useRef<Record<string, ProductQuantityInputValue>>({});
    const receivedOnFocusRef = useRef<Record<string, number>>({});
    const lastReceivedInputRef = useRef<Record<string, ProductQuantityInputValue>>({});

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((product) => {
            if (product.ID === id) {
                const updatedProduct = { ...product };
                updatedProduct[key] = value;
                return updatedProduct;
            }
            return product;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const handleQuantityFocus = (name: string, currentQuantity: number) => {
        quantityOnFocusRef.current[name] = currentQuantity;
        lastQuantityInputRef.current[name] = currentQuantity;
    };

    const handleQuantityChange = (
        value: string | number | undefined | null,
        error: boolean,
        id: number,
        name: string
    ) => {
        const inputValue = toProductQuantityInputValue(value);
        lastQuantityInputRef.current[name] = inputValue;
        if (!shouldApplyProductQuantityInput(inputValue)) {
            return;
        }
        changeValue('QUANTITY', inputValue, error, id);
    };

    const handleQuantityBlur = (id: number, name: string) => {
        const lastValue = lastQuantityInputRef.current[name] ?? quantityOnFocusRef.current[name] ?? 'empty';
        if (!shouldRestoreProductQuantityOnBlur(lastValue)) {
            return;
        }
        const quantityOnFocus = quantityOnFocusRef.current[name] ?? 0;
        changeValue('QUANTITY', quantityOnFocus, false, id);
    };

    const handleReceivedFocus = (name: string, currentReceived: number) => {
        receivedOnFocusRef.current[name] = currentReceived;
        lastReceivedInputRef.current[name] = currentReceived;
    };

    const handleReceivedChange = (
        value: string | number | undefined | null,
        error: boolean,
        id: number,
        name: string
    ) => {
        if (isEmptyProductQuantityInput(value)) {
            lastReceivedInputRef.current[name] = 'empty';
            return;
        }
        const inputValue = toProductQuantityInputValue(value);
        lastReceivedInputRef.current[name] = inputValue;
        if (!shouldApplyNonNegativeProductQuantityInput(inputValue)) {
            return;
        }
        changeValue('RECEIVED', inputValue, error, id);
    };

    const handleReceivedBlur = (id: number, name: string) => {
        const lastValue = lastReceivedInputRef.current[name] ?? receivedOnFocusRef.current[name] ?? 'empty';
        if (!shouldRestoreNonNegativeProductQuantityOnBlur(lastValue)) {
            return;
        }
        const receivedOnFocus = receivedOnFocusRef.current[name] ?? 0;
        changeValue('RECEIVED', receivedOnFocus, false, id);
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        const disabledRow = row.LINE_STATE === 'D';
        switch (column.key) {
            case 'NAME':
                return <ProductNameLink productId={row.PRODUCT_ID} name={row.NAME} />;
            case 'QUANTITY': {
                const disabled = !!props.disableEdit || disabledRow;
                return (
                    <TextFieldDebounced
                        value={row.QUANTITY}
                        error={props.editErrors[name]}
                        onChange={(value, error) => handleQuantityChange(value, error, row.ID, name)}
                        onFocus={() => handleQuantityFocus(name, Number(row.QUANTITY || 0))}
                        onBlur={() => handleQuantityBlur(row.ID, name)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={1}
                        step="1"
                        required={!disabled}
                        disabled={disabled}
                    />
                );
            }
            case 'RECEIVED': {
                const receivedName = getInputName(row.ID, column.key);
                return (
                    <TextFieldDebounced
                        value={row.RECEIVED}
                        error={props.editErrors[receivedName]}
                        onChange={(value, error) => handleReceivedChange(value, error, row.ID, receivedName)}
                        onFocus={() =>
                            handleReceivedFocus(receivedName, Number(row.RECEIVED || 0))
                        }
                        onBlur={() => handleReceivedBlur(row.ID, receivedName)}
                        name={receivedName}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        step="1"
                        disabled={props.newReturn || props.disableEdit || disabledRow || !props.permissionEditAll}
                    />
                );
            }
            case 'SERIAL_NUMBER':
                return (
                    <TextFieldDebounced
                        value={row.SERIAL_NUMBER}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('SERIAL_NUMBER', value, error, row.ID)}
                        name={name}
                        fullWidth
                        disabled={props.newReturn || props.disableEdit || disabledRow || !props.permissionEditAll}
                    />
                );
            case 'RETURN_REASON': {
                const editableReasonCodes = props.retunReasonCodes.filter(
                    (item) => ['exchange', 'changemind'].indexOf(String(item.value)) < 0
                );
                const returnReasonLabel = props.retunReasonCodes.find(
                    (option) => option.value == row.RETURN_REASON
                )?.label;

                return (
                    <Select
                        options={props.retunReasonCodes}
                        editableOptions={editableReasonCodes}
                        value={row.RETURN_REASON}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('RETURN_REASON', value, error, row.ID)}
                        name={name}
                        required={!disabledRow && !!Number(row.QUANTITY)}
                        fullWidth
                        emptyOption
                        disabled={props.disableEdit || disabledRow}
                        sx={resolutionSelectSx}
                        tooltiop={returnReasonLabel}
                    />
                );
            }
            case 'RESOLUTION': {
                const resolutionLabel = props.returnResolutionCodes.find(
                    (option) => option.value == row.RESOLUTION
                )?.label;
                const resolutionDisabled =
                    props.disableEdit || disabledRow || !props.skladonUser;

                return (
                    <Select
                        options={props.returnResolutionCodes}
                        value={row.RESOLUTION}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('RESOLUTION', value, error, row.ID)}
                        name={name}
                        required={!resolutionDisabled && !disabledRow && !!Number(row.QUANTITY)}
                        fullWidth
                        emptyOption
                        disabled={resolutionDisabled}
                        sx={resolutionSelectSx}
                        tooltiop={resolutionLabel}
                    />
                );
            }
            default:
                return null;
        }
    };

    const handleToggleRow = (row: TableRowType) => {
        const newLineState = row.LINE_STATE === 'D' ? 'E' : 'D';

        if (row.ID < 0) {
            if (row.LINE_STATE === 'D') {
                props.onChange(
                    props.data?.map((item) => {
                        if (item.ID === row.ID) {
                            return { ...item, LINE_STATE: 'E' };
                        }
                        return item;
                    }),
                    false
                );
            } else {
                props.onChange(
                    props.data?.filter((item) => item.ID != row.ID),
                    false
                );
            }
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (item.ID === row.ID) {
                        return { ...item, LINE_STATE: newLineState };
                    }
                    return item;
                }),
                false
            );
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="returnDetail.productTable"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            dataTestId="return-detail-products-table"
            userSettingsName="showReturnDetailProductsTableColumns"
            userSettingsData={userSettingsData}
            editUserSettings={editUserSettings}
            rowActions={[
                {
                    icon: <HighlightOffIcon />,
                    label: translate('returnDetail.productTable.action.removeProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'D',
                    style: { height: sizes.table_action_with_input_H },
                },
                {
                    icon: <AddCircleIcon />,
                    label: translate('returnDetail.productTable.action.addProduct'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'E',
                    style: { height: sizes.table_action_with_input_H },
                },
            ]}
            selectOptions={{
                returnResolutionCodes: props.returnResolutionCodes,
                retunReasonCodes: props.retunReasonCodes,
                productTypeCodes,
                expeditionMethodCodes,
                productInsetTypeCodes,
                boolNumberCodes,
                scanImeiSnCodes,
                batteryTypeCodes,
                currencyCodes,
            }}
            optionStyleFce={colorOptionFce}
            customRender={customRender}
            rowSxFce={(row) =>
                row.LINE_STATE === 'D'
                    ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                    : {}
            }
            disableFilter
            maxHeight={'unset'}
            sumCols={{ COUNT: true, QUANTITY: true, RECEIVED: true }}
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
        />
    );
};

export default ProductsTable;
